// Real brand marks — one SVG per brand SLUG, dropped into this folder by hand
// (no fetching, no CDN: the demo must read offline) and registered here,
// because SVGR needs static imports. A slug with no entry falls back to the
// monogram initials in `components/LogoTile` — so coverage can grow one
// brand at a time and nothing breaks for the rest.
//
// File contract (the Simple Icons shape, simpleicons.org): a square viewBox,
// one or more paths, NO fill attribute and NO background rect — the tile
// paints the mark through `currentColor`, and since 2026-09-08 that ink is
// the same for every brand (BLACK AND WHITE, user call: `.logotile` in
// index.css), so the marks read as one system on the dark ground. A file
// that carries its own fill or a background rect would opt itself out of
// that, which is why the contract bans both. Symbols by preference (tiles
// are 22–42px); wordmarks are
// allowed at the user's call and listed in WIDE_MARKS so they get the wider
// footprint a letterform strip needs to read at all.
//
// PNG fallback (BRAND_IMAGES, 2026-09-08): some houses have no usable vector
// anywhere, and the user drops a bitmap instead. A raster mark cannot take
// `currentColor`, so the second contract is BLACK INK ON A TRANSPARENT
// GROUND, TIGHT-CROPPED to the ink, and `.logo-img` inverts it to the same
// white the vector marks get. Two consequences worth knowing: the shape must
// live in the ALPHA channel (a white ground would invert to a black box), and
// a bitmap will not follow the tile's ink if the palette ever changes — which
// is why an SVG still wins whenever one exists. Keep the long edge ~600px:
// tiles are 22–42px, so anything larger is bundle weight for nothing.
//
// "The vector wins" is about the same mark in two formats, and starbucks is
// the case that shows the difference (user call 2026-09-08: "use the new
// starbucks png — the current is inverted"). The Simple Icons glyph paints
// the DISC as the ink, so on the then-black tile it read as a solid white
// puck with the siren knocked out of it; the user wanted the other
// rendition, the one the cups carry — a thin ring with the siren drawn in
// line on the tile's own black. No vector of it existed here, so the bitmap
// was the only carrier of the right shape, and `starbucks.svg` was DELETED
// rather than left in place, because it would have won the lookup in
// LogoTile and stranded the PNG silently.
//
// That call was REVERSED 2026-09-11, by the user dropping a new file: the
// solid DISC again, the siren knocked out in white. The tiles had turned
// white on the paper ground in between, which is the ground the ring was
// never chosen on. The lesson about the lookup order stands — a vector of
// this rendition would win over the PNG, so if one is ever dropped back in,
// the PNG must go the same way the SVG did.
//
// Provenance (2026-09-06): eleven are the `simple-icons` package's own files
// (nike mcdonalds red-bull uniqlo apple netflix instagram duolingo anthropic
// tesla ferrari — starbucks was a twelfth until its glyph turned out to be
// the wrong rendition, see above); five are symbols cut out of Wikimedia Commons
// logo files and flattened to one colour (louis-vuitton monogram, gucci GG,
// valentino V, gap letters, lululemon disc — the omega is a hole in it, so
// that tile is the one painted dark-on-light); five are user-dropped
// wordmarks (zara, coca-cola, dior from Simple Icons; tiffany, prada from a
// vector-logo site, cut free of the white background square those files
// carry); three more user-dropped and cut here: chanel (the interlocking
// Cs), 7-eleven (the 7 + ELEVEN, flattened from its three colours to one),
// aesop (the wordmark, ® dropped).
//
// 2026-09-08: seven the earlier pass gave up on arrived from the user — two
// vectors (hermes, the wordmark over PARIS; rhode, the lowercase wordmark)
// and five PNGs (bottega-veneta, giorgio-armani, saint-laurent, sephora,
// pandora). All were normalized on the way in rather than taken as dropped:
// the bitmaps were tight-cropped to their ink (giorgio-armani floated in
// 366px of padding, pandora in 1372px), downscaled to a 600px long edge and
// re-encoded — 1.1MB became 67KB, giorgio-armani alone having shipped
// uncompressed at 978KB. rhode came off a stock-vector site with the site's
// own WATERMARK in the file (82 white shapes under `<g id="watermark">`, at
// 0.61 opacity across the bottom of a 1000×1000 canvas) around 5 grey paths
// that were the actual logo: watermark dropped, `fill:#706D67` stripped so
// currentColor lands, viewBox tightened to the ink, 38.7KB → 4.5KB. Check a
// stock file for this — shipping someone's watermark is both ugly and theirs.
// gentle-monster is now the only styled brand still on its monogram.
//
// starbucks joined the bitmaps later the same day, dropped as a 416×737
// phone-sized image of the mark in WHITE on an opaque black canvas.
// Normalizing that is the mirror of the usual job: luminance became the ALPHA
// channel (the white ink turned into black ink on nothing, and the black gaps
// inside the siren became holes that read as the tile's own ground), levels
// clipped the encoder's noise floor, and the ink was cropped out of its
// padding — 416×737 and 65KB in, a square 234×234 and 15KB out.
//
// The disc that replaced it (2026-09-11, "make this one bigger") arrived as
// 980×980 with alpha, the 630px disc floating in 175px of padding a side —
// which was most of the smallness: at the tile's 68% the disc itself was
// painting at ~44%. Cropped to the ink it fills its tier, and on the sheet
// that is level with McDonald's, Red Bull and Nike; 74% made it the loudest
// symbol on the board, so the tier did not move. 134KB in, 29KB out. This
// file taught the normalizer a rule: the siren's eyes, nose and lips are
// three tiny black ISLANDS inside the white of the face, and the plain
// "drop anything under 3% of the largest component" pass erased them — a
// small component is a stray only when it sits OUTSIDE a large one's box.
//
// 2026-09-08, later: twenty-three more for the PHANTOM members of the three
// filled board lists (mock/phantomBrands.js — mock rows that live inside a
// list and nowhere else). They are marks like any other, so they go through
// the same two registries and the same contract: seven from Simple Icons
// (adidas toyota honda ford new-balance porsche bmw), thirteen from Wikimedia
// (nestle procter-gamble prime-hydration celsius liquid-death poppi
// trader-joes shein levis rolex loro-piana mercedes-benz, plus the
// user-dropped ralph-lauren) with their fills stripped, their gradients
// flattened (celsius shipped its wordmark on a blue→pink linearGradient) and
// their viewBoxes tightened to the ink where the source left padding
// (prime-hydration, trader-joes, ralph-lauren), and four bitmaps for the
// brands with no vector anywhere: cerave and la-mer (white ground → luminance
// became the alpha channel), elf-beauty and selkirk (already black on
// transparent, cropped).
//
// Mercedes is the size lesson: the obvious Commons file is the star with the
// wordmark, 489 paths and 286KB after normalizing — for a 40px tile. The
// plain star ("Mercedes-Benz free logo.svg") is 1.5KB and reads better small.
// Check the byte count as well as the shape.
//
// Two renditions were REJECTED on the contact sheet rather than shipped
// small: celsius arrived as a roundel+wordmark lockup and was cropped by its
// viewBox to the roundel alone (symbol over wordmark), and Levi's arrived as
// the batwing, whose knocked-out letters flood to a solid blob once the fills
// go — the plain wordmark replaced it. Ralph Lauren's Commons wordmark went
// the same way: a vector, but 14:1 and a hairline smear at 40px.
//
// The user then dropped the three that pass had given up on (2026-09-08):
// ralph-lauren as a vector, selkirk and la-mer as PNGs. All three needed the
// usual normalizing — ralph-lauren arrived with a white BACKGROUND PATH
// (`M0 0h192.744v192.744H0V0z`, the banned background rect drawn as a path)
// behind a wordmark floating in a square artboard, so the path went and the
// viewBox was cropped to the ink, 6.5:1; la-mer was black on an opaque white
// square, so luminance became the alpha channel; selkirk was already black on
// transparent and only needed cropping. The file also arrived as
// `ralph-laurent.svg` — a slug that would never have been found — and was
// renamed. Two phantoms are still on monogram initials — on-running (whose
// mark IS the word "On", so the tile loses nothing) and olaplex — with their
// letters spelled out in config/vocab so neither takes the generic
// first-two-letters fallback.
//
// ugg (2026-09-10, the brand joined the board the day before) arrived in
// la-mer's shape exactly: black ink on an OPAQUE white 600² square with the
// wordmark floating in the middle of it. Untouched it would have painted a
// white square on the tile — `filter: invert(1)` inverts the ground too —
// so luminance became the alpha channel again and the canvas was cropped to
// the ink, 540×246. It is SNUG rather than wide: at 2.2:1 it is marvel's
// twin, a short heavy lockup whose caps would outgrow every symbol beside
// it at the full 88%.
//
// crocs (2026-09-10, same day — it is not on the board but fronts an UGG
// briefing tile as the rival, so the tile wanted the mark) arrived the same
// way once more: black on an OPAQUE white 600² square, no alpha, the
// wordmark floating in the middle with a ™ at its shoulder. Luminance
// became the alpha channel, the ™ went (two stray components of 73 and 156
// pixels beside five letters of ~6,000 each — anything under 3% of the
// largest component is dropped on the way in), and the canvas was cropped
// to the ink: 540×101. That is a 5.35:1 strip, so it is WIDE, not snug.
// No PIL or ImageMagick on this machine: the normalizer is a hand-written
// Node PNG decode/encode over zlib, kept as `scripts/normalize-mark.js`.
//
// ouai (2026-09-11) came off a stock site as a 4-bit PALETTE PNG with the
// site's "transparency" CHECKERBOARD baked into the pixels — the mark on a
// grid of white and #ccc squares, no alpha at all. The normalizer grew two
// things for it: palette / sub-8-bit decoding, and an explicit ink window
// (full ink at luminance ≤ 60, ground at ≥ 190) so the checkerboard reads
// as ground instead of a grey haze. 920×296 in, 668×197 out — four thin
// geometric caps at 3.39:1. It shipped WIDE on that ratio and came straight
// back as "a little bit too big" (user, same day): only four letters, each
// nearly as tall as it is wide, so the full width bought more cap height
// than the seven-letter strips beside it. SNUG now — the tier note's rule
// ("aspect ratio is the tell, but the contact sheet is the judge") earning
// its keep.
//
// diageo (2026-09-12, the brand joined the board the same day) was ouai's
// twin on the way in — a 4-bit palette PNG from the same kind of stock site,
// 900×500 with the wordmark floating in padding — but clean: plain black on
// plain white, no checkerboard, no ™, so the default ink window did the job.
// Six components, one per letter (the A's counter lies inside the A's box,
// so the stray rule left it alone), cropped to the ink: 863×184, 4.69:1 —
// six heavy geometric caps, skims' and pandora's neighbour in ratio, so
// WIDE on the same reasoning and a contact-sheet check against them.
//
// estee-lauder (2026-09-25, the brand joined the board 2026-09-24) arrived
// as diageo did: an 8-bit palette PNG, black caps floating on an opaque
// white 600² square. Luminance became the alpha channel, the canvas was
// cropped to the ink: 540×68, 7.94:1 — a thin letterspaced strip between
// rolex and trader-joes in ratio, so WIDE. The macron over the second E is
// a 33×4px component sitting OUTSIDE the E's box, and it survived only
// because it clears the 3% floor (132px against the R's 1067) — a smaller
// diacritic would need the floor lowered, so check the output for accents.
// REPLACED 2026-10-05 by the user's own drop: the EL MONOGRAM — the script
// initials knocked out of a black square inside a hairline frame, 1024²
// with padding (and named `estee_lauder.png`; renamed to the slug). Cropped
// to the frame, 720×722 — a square symbol, so it left WIDE_MARKS for the
// base tier and the header draws it in the puck instead of initials.
//
// adidas and dr-martens (2026-10-06, both joined the fashion board
// 2026-10-05) arrived as the user's own drops, two more PNGs. adidas was a
// Simple Icons VECTOR until then — the three-stripe mountain alone, one of
// the 2026-09-08 phantoms — and the user dropped the LOCKUP instead, the
// stripes stacked over the lowercase wordmark: 1440×970 grey+alpha, already
// black on transparent and tight to its ink, so it only needed the 600px
// long edge (600×404, 43KB → 16KB). It is starbucks' lesson again: the
// vector would have won the lookup and stranded the PNG, so `adidas.svg`
// was DELETED. dr-martens came as `dr-marteens.png` — a slug nothing looks
// up, renamed — an 8-bit palette PNG with the full lockup (Dr. AirWair
// Martens over the sole arc, "with Bouncing soles" under it) in near-black
// on opaque white. Luminance became the alpha channel as usual, but the
// stray rule had to be TURNED OFF (`--stray=0`): the lockup is fifty-seven
// components, and the 3% floor read twenty-nine of them — AirWair's
// letters, the speed lines, the small words — as ™-sized specks and
// erased them. 1000×585 → 565×313 once cropped, 1.81:1, 17KB → 20KB.
//
// Adding a brand: put `<slug>.svg` beside this file (the SLUG — so
// `coca-cola.svg`, not `cocacola.svg`), import it below, add the slug to
// BRAND_MARKS (and to a size set if it is not the common case). A bitmap
// goes through BRAND_IMAGES instead, under the PNG contract above. Slugs are
// the API's (`config/vocab.js`).
import { ReactComponent as SevenEleven } from "assets/7-eleven.svg";
import { ReactComponent as Aesop } from "assets/aesop.svg";
import { ReactComponent as Anthropic } from "assets/anthropic.svg";
import { ReactComponent as Apple } from "assets/apple.svg";
import { ReactComponent as Bmw } from "assets/bmw.svg";
import { ReactComponent as Cartier } from "assets/cartier.svg";
import { ReactComponent as Celsius } from "assets/celsius.svg";
import { ReactComponent as Chanel } from "assets/chanel.svg";
import { ReactComponent as CocaCola } from "assets/coca-cola.svg";
import { ReactComponent as Depop } from "assets/depop.svg";
import { ReactComponent as Dior } from "assets/dior.svg";
import { ReactComponent as Duolingo } from "assets/duolingo.svg";
import { ReactComponent as Ferrari } from "assets/ferrari.svg";
import { ReactComponent as Ford } from "assets/ford.svg";
import { ReactComponent as Gap } from "assets/gap.svg";
import { ReactComponent as Gucci } from "assets/gucci.svg";
import { ReactComponent as Hermes } from "assets/hermes.svg";
import { ReactComponent as Honda } from "assets/honda.svg";
import { ReactComponent as Instagram } from "assets/instagram.svg";
import { ReactComponent as Levis } from "assets/levis.svg";
import { ReactComponent as LiquidDeath } from "assets/liquid-death.svg";
import { ReactComponent as LoroPiana } from "assets/loro-piana.svg";
import { ReactComponent as LouisVuitton } from "assets/louis-vuitton.svg";
import { ReactComponent as Lululemon } from "assets/lululemon.svg";
import { ReactComponent as McDonalds } from "assets/mcdonalds.svg";
import { ReactComponent as MercedesBenz } from "assets/mercedes-benz.svg";
import { ReactComponent as MiuMiu } from "assets/miu-miu.svg";
import { ReactComponent as Nestle } from "assets/nestle.svg";
import { ReactComponent as Netflix } from "assets/netflix.svg";
import { ReactComponent as NewBalance } from "assets/new-balance.svg";
import { ReactComponent as Nike } from "assets/nike.svg";
import { ReactComponent as OnRunning } from "assets/on-running.svg";
import { ReactComponent as Poppi } from "assets/poppi.svg";
import { ReactComponent as Porsche } from "assets/porsche.svg";
import { ReactComponent as Prada } from "assets/prada.svg";
import { ReactComponent as Prime } from "assets/prime-hydration.svg";
import { ReactComponent as ProcterGamble } from "assets/procter-gamble.svg";
import { ReactComponent as RalphLauren } from "assets/ralph-lauren.svg";
import { ReactComponent as RedBull } from "assets/red-bull.svg";
import { ReactComponent as Rhode } from "assets/rhode.svg";
import { ReactComponent as Rolex } from "assets/rolex.svg";
import { ReactComponent as Shein } from "assets/shein.svg";
import { ReactComponent as Tesla } from "assets/tesla.svg";
import { ReactComponent as Tiffany } from "assets/tiffany.svg";
import { ReactComponent as TikTok } from "assets/tiktok.svg";
import { ReactComponent as Toyota } from "assets/toyota.svg";
import { ReactComponent as TraderJoes } from "assets/trader-joes.svg";
import { ReactComponent as Uniqlo } from "assets/uniqlo.svg";
import { ReactComponent as Valentino } from "assets/valentino.svg";
import { ReactComponent as Zara } from "assets/zara.svg";

// bitmaps (see the PNG contract above) — these import as URLs, not components
import adidasImg from "assets/adidas.png";
import bottegaVenetaImg from "assets/bottega-veneta.png";
import ceraveImg from "assets/cerave.png";
import crocsImg from "assets/crocs.png";
import diageoImg from "assets/diageo.png";
import drMartensImg from "assets/dr-martens.png";
import elfBeautyImg from "assets/elf-beauty.png";
import esteeLauderImg from "assets/estee-lauder.png";
import giorgioArmaniImg from "assets/giorgio-armani.png";
import laMerImg from "assets/la-mer.png";
import marvelImg from "assets/marvel.png";
import ouaiImg from "assets/ouai.png";
import pandoraImg from "assets/pandora.png";
import saintLaurentImg from "assets/saint-laurent.png";
import selkirkImg from "assets/selkirk.png";
import skimsImg from "assets/skims.png";
import sephoraImg from "assets/sephora.png";
import starbucksImg from "assets/starbucks.png";
import uggImg from "assets/ugg.png";

export const BRAND_MARKS = {
  "7-eleven": SevenEleven,
  aesop: Aesop,
  anthropic: Anthropic,
  apple: Apple,
  bmw: Bmw,
  cartier: Cartier,
  celsius: Celsius,
  chanel: Chanel,
  "coca-cola": CocaCola,
  depop: Depop,
  dior: Dior,
  duolingo: Duolingo,
  ferrari: Ferrari,
  ford: Ford,
  gap: Gap,
  gucci: Gucci,
  hermes: Hermes,
  honda: Honda,
  instagram: Instagram,
  levis: Levis,
  "liquid-death": LiquidDeath,
  "loro-piana": LoroPiana,
  "louis-vuitton": LouisVuitton,
  lululemon: Lululemon,
  mcdonalds: McDonalds,
  "mercedes-benz": MercedesBenz,
  "miu-miu": MiuMiu,
  nestle: Nestle,
  netflix: Netflix,
  "new-balance": NewBalance,
  nike: Nike,
  "on-running": OnRunning,
  poppi: Poppi,
  porsche: Porsche,
  prada: Prada,
  "prime-hydration": Prime,
  "procter-gamble": ProcterGamble,
  "ralph-lauren": RalphLauren,
  "red-bull": RedBull,
  rhode: Rhode,
  rolex: Rolex,
  shein: Shein,
  tesla: Tesla,
  tiffany: Tiffany,
  tiktok: TikTok,
  toyota: Toyota,
  "trader-joes": TraderJoes,
  uniqlo: Uniqlo,
  valentino: Valentino,
  zara: Zara,
};

// the raster half of the registry — same job, different paint route (the
// tile inverts these rather than filling them with currentColor)
export const BRAND_IMAGES = {
  adidas: adidasImg,
  "bottega-veneta": bottegaVenetaImg,
  cerave: ceraveImg,
  crocs: crocsImg,
  diageo: diageoImg,
  "dr-martens": drMartensImg,
  "elf-beauty": elfBeautyImg,
  "estee-lauder": esteeLauderImg,
  "giorgio-armani": giorgioArmaniImg,
  "la-mer": laMerImg,
  marvel: marvelImg,
  ouai: ouaiImg,
  pandora: pandoraImg,
  "saint-laurent": saintLaurentImg,
  selkirk: selkirkImg,
  skims: skimsImg,
  sephora: sephoraImg,
  starbucks: starbucksImg,
  ugg: uggImg,
};

// OPTICAL SIZING (2026-09-08, user call: "make some logos which are too big
// smaller — mcdonalds ferrari louis vuitton netflix apple…"). One percentage
// cannot fit every mark, because the tile scales a mark's BOX while the eye
// reads its INK. A Simple Icons glyph fills its own square viewBox, so at a
// given box size a square silhouette covers roughly three times the area of
// a 3:1 wordmark strip and reads far heavier. Three tiers, measured off each
// file's ink bounding box and then checked on a contact sheet at 40px:
//
//   base (56%)  — a symbol that fills its viewBox: the common case, so it is
//                 the DEFAULT and a newly dropped file needs no entry here.
//                 A TALL mark belongs here too: the tier caps the long edge,
//                 and for a portrait silhouette that edge is its height, so
//                 anything larger makes it the tallest ink on the board.
//   roomy (68%) — a symbol with generous built-in padding, or a thin or
//                 circular silhouette that reads small at the base size.
//   snug (74%)  — a COMPACT wordmark: short and heavy rather than a long thin
//                 strip, so the full width would grow its letters taller than
//                 the symbols beside it.
//   wide (88%)  — a wordmark: a letterform strip in a square canvas, which
//                 is mostly empty vertically and smears at anything less.
//
// Between snug and wide the question is not "is it a wordmark" but how much
// INK the width buys: a 2:1 lockup at 88% has letters twice the height of a
// 4:1 strip at the same width. Aspect ratio is the tell, but the contact
// sheet is the judge.
//
// Adding a mark: drop it in at the default, look at the tile, and only then
// reach for a set. Get it wrong and the brand simply looks louder or
// quieter than its neighbours — the tile itself never breaks.
//
// The tiers read the same for a bitmap, which is the other reason PNGs are
// tight-cropped on the way in: a mark whose ink fills its canvas behaves
// like a glyph that fills its viewBox, so the tier sets the LONG EDGE either
// way and one set of numbers covers both registries.
export const WIDE_MARKS = new Set([
  "aesop",
  "bottega-veneta", // 9.5:1 — the widest strip here by some way
  "cartier", // the script wordmark, ~3.6:1
  "cerave", // the wordmark, ~2.8:1 and lightly drawn
  "coca-cola",
  "crocs", // the lowercase wordmark, 5.35:1 once cropped (the user's bitmap)
  "diageo", // six heavy caps, 4.69:1 once cropped (the user's bitmap)
  "dior",
  "dr-martens", // the full lockup — Dr. AirWair Martens over the sole arc,
  // 1.81:1 — a busy drawing of small words and lines that is a smudge below
  // the full width (the user's bitmap); giorgio-armani's case
  "giorgio-armani", // eagle stacked over the wordmark, ~2:1
  "hermes", // the wordmark over PARIS
  "depop", // the wordmark, ~3.9:1
  "la-mer", // thin caps, ~3:1 — the width is what makes them read
  "loro-piana", // crest over a script wordmark, ~4.7:1
  "miu-miu", // the lowercase wordmark, ~6.4:1
  "pandora", // crown + wordmark, ~4.8:1
  "prada",
  "ralph-lauren", // the script wordmark, 6.5:1 once cropped to its ink
  "rolex", // the wordmark, 5.5:1
  "shein", // letterspaced caps, ~4.8:1
  "skims", // heavy italic caps, ~4.4:1 (the user's bitmap)
  "tiffany",
  "trader-joes", // 9.2:1 — bottega's neighbour at the far end
]);
// held back from the full strip (user call 2026-09-08: "zara and rhode should
// be a little bit smaller") — both are short, heavy wordmarks whose letters
// outgrew every symbol on the board at 88%. A mark lands here on CAP HEIGHT,
// not on weight: ouai is a thin mark that arrived at 88% and still read as
// the loudest tile in its row (user call 2026-09-11, "a little bit too big"),
// because four near-square letters spend the whole width on letter height —
// the contact-sheet question in the tier note above, answered.
export const SNUG_MARKS = new Set([
  "adidas", // the stripes stacked over the lowercase wordmark, 1.49:1 (the
  // user's bitmap) — a compact block like ugg and marvel: at wide it was
  // the loudest tile in its row, at roomy the wordmark under the stripes
  // went to a hairline
  "levis", // the batwing-tab wordmark, ~2.4:1 and very heavy
  "marvel", // the box silhouette, ~2.2:1 and solid
  "ouai", // four thin geometric caps, 3.39:1 — long ENOUGH to look wide, but
  // only four letters, so 88% grew them past cerave and crocs beside it
  "poppi", // heavy lowercase, rhode's twin in silhouette
  "prime-hydration", // short heavy caps, ~2.3:1
  "procter-gamble", // the P&G lockup, ~2.3:1 and very heavy
  "rhode", // heavy lowercase with a big x-height
  "selkirk", // paddle mark stacked over the word — taller than a strip
  "ugg", // three heavy overlapping caps, ~2.2:1 — marvel's twin in weight
  "zara", // four tight caps, the shortest wordmark here
]);
export const ROOMY_MARKS = new Set([
  "celsius", // the roundel alone (the wordmark is cropped off in the file) —
  // a circle of thin ink, starbucks' case exactly
  "chanel", // the cut file carries its own margin
  "elf-beauty", // the lockup floats in its own square of air
  "ford", // the script inside a thin oval — the flattest mark here
  "liquid-death", // a dense stacked wordmark: legible only with the extra 12%
  "nestle", // fine line art (the nest) — it disappears at the base size
  "new-balance", // the NB monogram is a 2:1 strip of thin ink
  "nike", // a low, thin swoosh — 56% reads as a scratch
  "red-bull", // two bulls and a disc, wide and light
  "starbucks", // circular — a solid disc since 2026-09-11 (a ring for the
  // three days before); a circle in a square box covers π/4 of it, so at the
  // base size it reads a step smaller than the square glyphs beside it
  "valentino", // the V sits inside an oval of whitespace
]);

// the mark component for a slug, or undefined → the tile tries a bitmap next
export const brandMark = (slug) => BRAND_MARKS[slug];
// the bitmap URL for a slug, or undefined → the tile shows initials
export const brandImage = (slug) => BRAND_IMAGES[slug];
// a real mark exists by either route — what callers and tests should ask
export const hasMark = (slug) => slug in BRAND_MARKS || slug in BRAND_IMAGES;
export const isWideMark = (slug) => WIDE_MARKS.has(slug);
export const isSnugMark = (slug) => SNUG_MARKS.has(slug);
export const isRoomyMark = (slug) => ROOMY_MARKS.has(slug);

// a bitmap whose ink is the DISC and the mark the HOLE in it (starbucks'
// 2026-09-11 file: a solid black puck, the siren knocked out to
// transparent). The invert that turns every other bitmap's black ink white
// on a dark ground turns this one inside out — a white puck with the siren
// showing the ground through it (user calls 2026-09-12: "the siren should
// be white and around it black, not vice versa", then "can the bg around
// the siren be transparent"). So a disc mark NEVER inverts: on a dark ground
// the CSS paints the HOLE instead — the element is white and masked to the
// disc's footprint minus the file's ink, which leaves the siren alone, white
// on nothing, like every other mark there; on paper the file is left as
// dropped, the black puck with the white siren. `.logo-img.disc` in
// index.css; LogoTile hands the file to the mask as `--mark-src`.
export const DISC_MARKS = new Set(["starbucks"]);
export const isDiscMark = (slug) => DISC_MARKS.has(slug);

// a bitmap whose ink is a BLOCK and the mark the HOLE in it, the square
// cousin of a disc mark (estee-lauder's 2026-10-05 file: a black square, the
// script EL knocked out to transparent). In the header's puck the block must
// not show — inverted it was a white square on the black puck with the
// letters dark (user call the same day: "it should be inverted in both the
// black and the white versions"). So the puck paints the HOLE in its own
// ink through a mask (`.puck-knock`, index.css): the letters white on the
// black puck, black on the white one. Tiles are untouched.
export const KNOCKOUT_MARKS = new Set(["estee-lauder"]);
export const isKnockoutMark = (slug) => KNOCKOUT_MARKS.has(slug);

// a WIDE wordmark that heads its workspace as the wordmark anyway, not as
// initials (user call 2026-10-05: "for tiffany & co I want to use its text
// logo in that button when tiffany is logged in"). The strip is drawn across
// the puck at the wide tier — small, and the user's choice. dr-martens
// joined 2026-11-02 (user call from a crop of its "DM" puck: "logo should be
// fixed to show dr martens") — the full lockup, at the tier PUCK_TIERS gives it.
export const HEADER_WORDMARKS = new Set(["tiffany", "dr-martens"]);

// a mark drawn at a DIFFERENT tier in the header puck than on its tiles
// (2026-11-02). The dr-martens lockup (1.81:1) is WIDE on a tile, but in the
// circle its corner ink — the D's top-left, the speed lines off the s —
// reached 0.495 of the diameter at wide's 88%, on the rim, and clipped;
// snug's 74% brings it to 0.42, the knockout's clearance, and the words still
// read (a contact sheet at 88 / 80 / 74 / 68 / 62 on both pucks). Tiles are
// untouched. PUCK_INSET_MARKS below is the smaller step for a boxy symbol.
export const PUCK_TIERS = { "dr-martens": "snug" };

// a mark drawn SMALLER in the header puck than its tile tier (2026-11-02,
// user call from a crop of the adidas puck: "padding on black adidas logo is
// off, add better padding"). A tier sizes the mark in a square tile; the
// puck is a circle, and a BOXY block's corners are what meet its rim. The
// adidas lockup (1.49:1) at snug's 74% put its corners 0.45 of the width
// from the centre, a hair inside the 0.5 rim; `inset` (62%, `.lab-brand-puck
// .logo-mark.inset`) brings them to 0.37, the clearance the knockout's 58%
// square was given for the same reason. Tiles are untouched.
export const PUCK_INSET_MARKS = new Set(["adidas"]);

// a workspace whose header puck wears the brand's OWN colour instead of the
// black / white pairing (user call 2026-10-06, from a reference crop: "for
// tiffany I don't want to use black and white bg, I want to use Tiffany's
// colour and black logo"). Opt-in and authored per brand, because a puck in
// a brand colour is a brand's claim, not a derivation: the ground must be
// a code the brand OWNS outright (Tiffany Blue, Pantone 1837, is the
// strongest single brand code on the board — config/vocab's note) and the
// ink must read on it (black on #81d8d0 is 12.6:1; white would be 1.7:1,
// which is why the reference's wordmark is black). The same pair on every
// ground, the 3.5 disc's rule — a brand colour does not flip on the dusk.
// #81d8d0 is Pantone 1837's published sRGB value and what the user's crop
// measures (rgb 129 216 208); the darker #0abab5 that brand-colour sites
// list, and that config/vocab's tile record carries, stood for an hour on
// 2026-10-06 (user call: "looks like we use some darker shade, check out
// the exact Tiffany shade") — it is NOT the robin's-egg blue of the box.
// `ink` colours a VECTOR mark, a KNOCKOUT's hole and the INITIALS (all
// currentColor); a BITMAP stays its file's black ink in a coloured puck
// (the invert that whitens it on the black puck is off there), so a bitmap
// brand listed here needs a ground that black reads on.
export const HEADER_INKS = {
  tiffany: { bg: "#81d8d0", ink: "#000" },
};

// the WORKSPACE's own mark in the HEADER (2026-09-29, user call from a
// reference crop: "when starbucks is the brand that logged in, instead of
// the 3.5 logo in the header I want the starbucks logo"; EVERY workspace
// since 2026-10-05 — "like the logo in the rounded button for starbucks, I
// want it for every brand that is logged in"). The box is a 40px round
// button in the product wordmark's place (components/Wordmark `brand`), so
// the rule is a workspace avatar's: the brand's SYMBOL when it has one, its
// initials otherwise, never a strip squeezed into a circle. Derived from
// the registries above — a new workspace brand needs no entry here:
//
//   disc   — a DISC mark is already the round button (starbucks): the file
//            as dropped, `.lab-brand-img.own.disc`.
//   vector / image — any other mark that is not WIDE, drawn in a puck at
//            its own optical tier (`.lab-brand-puck`).
//   knockout — a KNOCKOUT bitmap (estee-lauder): its hole painted in the
//            puck's ink, the block itself never shown (`.puck-knock`).
//   mono   — a WIDE wordmark (hermes, prada: a few px tall at this
//            size) or no mark at all: the monogram initials in the puck,
//            the way LogoTile falls back. Drop a symbol-shaped file for the
//            slug and it takes the initials' place — or list the slug in
//            HEADER_WORDMARKS to draw the strip regardless (tiffany).
//
// Every puck kind may carry `ink` — the brand's own `{bg, ink}` pair from
// HEADER_INKS (tiffany) — which the puck wears instead of black / white.
//
// No slug (the wall, the plain board) → undefined, the 3.5 disc.
export const headerMark = (slug) => {
  if (!slug) return undefined;
  if (isDiscMark(slug)) return { kind: "disc", src: BRAND_IMAGES[slug] };
  const ink = HEADER_INKS[slug];
  const wide = isWideMark(slug);
  if ((wide && !HEADER_WORDMARKS.has(slug)) || !hasMark(slug))
    return { kind: "mono", ink };
  const size =
    PUCK_TIERS[slug] ??
    (wide
      ? "wide"
      : isSnugMark(slug)
        ? "snug"
        : isRoomyMark(slug)
          ? "roomy"
          : "");
  const fit = PUCK_INSET_MARKS.has(slug) ? `${size} inset`.trim() : size;
  const Mark = BRAND_MARKS[slug];
  return Mark
    ? { kind: "vector", Mark, size: fit, ink }
    : {
        kind: isKnockoutMark(slug) ? "knockout" : "image",
        src: BRAND_IMAGES[slug],
        size: fit,
        ink,
      };
};
// saint-laurent is the notable BASE entry: the YSL monogram is the one
// portrait mark here (1:2), and a tall silhouette spends the tier on height —
// at roomy it was the tallest ink on the board, so it sits at the default.
